import PropTypes from 'prop-types'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import { Box, Button, DashboardModule } from '@domain-group/suit-theme'

import { useFilters } from '@domain-group/react-hooks'

import useAgencyPublication from '@hooks/useAgencyPublication'
import useAgencyPublications from '@hooks/useAgencyPublications'
import useSelectEntities from '@hooks/useSelectEntities'

import { confirmDelete } from '@functions/entity'

import DashboardModuleFilters from '@components/DashboardModuleFilters'
import ScopedConditionalContent from '@components/ScopedConditionalContent'

import AgencyPublicationListItem from './AgencyPublicationListItem'

const buildPageFilterFields = () => {
  const style = {
    marginBottom: 16,
  }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'publication_string',
      label: 'Publication Title or ID',
    },
  ]
}

const AgencyPublications = (props) => {
  const { agencyId } = props

  const filterPayload = useFilters()
  const {
    pageFilters,
  } = filterPayload

  const agencyPublicationsPayload = useAgencyPublications({
    filters: { ...pageFilters, agency_id: agencyId },
    performHttpRequests: !!agencyId,
    requestOptions: {
      include_agency_publication_publication: true,
      include_publication_provider: true,
    },
    sortKey: 'publication_id',
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredAgencyPublicationCount,
    filteredAgencyPublications,
    hasFilteredAgencyPublications,
    loading,
  } = agencyPublicationsPayload

  const {
    callbacks: { addAgencyPublications, bulkDeleteAgencyPublications },
    deleting,
  } = useAgencyPublication({ agency_id: agencyId })

  const unselectEntitiesPayload = useSelectEntities()
  const {
    callbacks: {
      getSelectableListItemProps,
      deselectAll,
    },
    hasMinimumSelected,
    selectedEntitiesCount,
    selectedEntityIds,
  } = unselectEntitiesPayload

  const confirmDeleteParams = {
    callbacks: {
      afterAction: deselectAll,
      buildActionParams: () => (
        [{ agency_publication_ids: selectedEntityIds, agency_id: agencyId }]
      ),
      deleteFn: bulkDeleteAgencyPublications,
    },
    options: { isBulkDelete: true },
    strings: {
      deleteString: 'unassign',
      entityName: 'Publications',
    },
  }

  return (
    <DashboardModule
      boxProps={{ id: 'realhub-nav-publications' }}
      contentBoxProps={{ flexDirection: 'column' }}
      headerRight={<DashboardModule.FilterHeader {...filterPayload} />}
      loading={loading}
      title="Publications"
    >
      <DashboardModuleFilters
        filterFields={buildPageFilterFields()}
        {...filterPayload}
      />

      <Box flexDirection="column">
        {!loading && !hasFilteredAgencyPublications && (
          <DashboardModule.BlankState>
            <DashboardModule.BlankState.Title>
              No Publications present
            </DashboardModule.BlankState.Title>

            <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
              <DashboardModule.BlankState.Paragraph>
                Please assign a Publication
              </DashboardModule.BlankState.Paragraph>
            </ScopedConditionalContent>
          </DashboardModule.BlankState>
        )}

        {!loading && hasFilteredAgencyPublications && (
          <DashboardModule.ScrollableSection maxHeight={600}>
            {filteredAgencyPublications.map((agencyPublication) => {
              const { id } = agencyPublication
              const listItemProps = getSelectableListItemProps(id)

              return (
                <AgencyPublicationListItem
                  agencyPublication={agencyPublication}
                  key={id}
                  {...listItemProps}
                />
              )
            })}
          </DashboardModule.ScrollableSection>
        )}

        <DashboardModule.LoadMoreFooter
          boxProps={{ borderTop: loading ? 'none' : '1px solid' }}
          callbacks={{ loadMore }}
          canLoadMore={canLoadMore}
          entityCount={filteredAgencyPublicationCount}
          loading={loading}
        >
          <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
            {hasMinimumSelected && (
              <Button
                buttonStyle="secondaryEdit"
                icon={<FontAwesomeIcon icon={faTrashAlt} />}
                loading={deleting}
                marginRight={[0, 'medium']}
                marginBottom={['medium', 0]}
                onClick={() => confirmDelete(confirmDeleteParams)}
                size={['medium', 'small']}
                width={['100%', 'fit-content']}
              >
                Unassign Selected Publications ({selectedEntitiesCount})
              </Button>
            )}

            <Button
              buttonStyle="secondaryEdit"
              icon={<FontAwesomeIcon icon={faPlus} />}
              onClick={() => addAgencyPublications()}
              size={['medium', 'small']}
              width={['100%', 'fit-content']}
            >
              Add Publications
            </Button>
          </ScopedConditionalContent>
        </DashboardModule.LoadMoreFooter>
      </Box>
    </DashboardModule>
  )
}

AgencyPublications.propTypes = {
  agencyId: PropTypes.number.isRequired,
}

export default AgencyPublications
